// frontend/src/contexts/AriaContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const AriaContext = createContext(null);

const PAGE_NAMES = {
  '/': 'Control Tower',
  '/orders': 'Sales Orders',
  '/factory': 'Factory',
  '/quality': 'Quality',
  '/inventory': 'Inventory',
  '/dispatch': 'Dispatch',
  '/finance': 'Finance',
  '/procurement': 'Procurement',
  '/customers': 'Customers',
  '/products': 'Products',
  '/genealogy': 'Genealogy',
};

export function AriaProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [visibleIds, setVisibleIds] = useState([]);
  const location = useLocation();
  const { user } = useAuth();

  const pageName = PAGE_NAMES[location.pathname] || location.pathname;
  const pageContext = { page: pageName, visible_ids: visibleIds };

  const openPanel = useCallback(() => { setIsOpen(true); setCommandBarOpen(false); }, []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(() => setIsOpen(p => !p), []);
  const openCommandBar = useCallback(() => setCommandBarOpen(true), []);
  const closeCommandBar = useCallback(() => setCommandBarOpen(false), []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandBarOpen(p => !p);
      }
      if (e.key === 'Escape') {
        setCommandBarOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const aiConfirm = user?.ai_confirm !== false;

  return (
    <AriaContext.Provider value={{
      isOpen, openPanel, closePanel, togglePanel,
      commandBarOpen, openCommandBar, closeCommandBar,
      pageContext, setVisibleIds,
      aiConfirm, user,
    }}>
      {children}
    </AriaContext.Provider>
  );
}

export const useAriaContext = () => useContext(AriaContext);
